import {Form, InputGroup} from "react-bootstrap";
import React from "react";
import {BoolSelect, CustomSwitch, DatePicker, Select} from "../components";
import PropTypes from "prop-types";

function PreWrappedInput(props) {
  return <InputGroup size="md">
    <InputGroup.Prepend>
      <InputGroup.Text style={{fontSize: "0.9rem"}}>{props.text}</InputGroup.Text>
    </InputGroup.Prepend>
    {props.children}
  </InputGroup>
}

const FormGroup = React.forwardRef((props, ref) => {
  const {name, onChange, value, disabled, errors, isValid, isInvalid, inputProps, ...remainingProps} = props
  const {label, text, children, input, placeholder, inputStyle, pre, rows, style, extraChildProps = {}, ...filteredProps} = remainingProps
  let childInputProps = {name, onChange, value, isValid, isInvalid, placeholder, style: inputStyle, ref, rows, ...extraChildProps}
  if (inputProps) {
    childInputProps = {...childInputProps, inputProps}
  }

  const inputFromProps = () => {
    if (['number', 'text', 'textarea', 'email', 'file'].includes(input)) {
      // as per React Bootstrap doc
      const keyForInput = input === 'textarea' ? 'as' : 'type';
      let inputProps = {
        ...childInputProps,
        disabled,
        ref,
        [keyForInput]: input
      }
      if (input === 'number') {
        inputProps = {
          ...inputProps,
          onWheel: (e) => e.target.blur()
        }
      }
      const formControlInput = <Form.Control {...inputProps} />;
      if (pre) {
        return <PreWrappedInput text={pre}>
          {formControlInput}
        </PreWrappedInput>
      } else {
        return formControlInput
      }
    } else {
      addDisabledToChildProps(input)
      return React.createElement(input, childInputProps, children)
    }
  }

  const inputFromChildren = () => (
    React.Children.map(children, child => {
      if (React.isValidElement(child)) {
        addDisabledToChildProps(child.type)
        return React.cloneElement(child, { ...childInputProps, ...child.props});
      }
    })
  )

  const addDisabledToChildProps = (inputType) => {
    if (inputType === Select || inputType === BoolSelect || inputType === DatePicker) {
      childInputProps = {...childInputProps, isDisabled: disabled}
    } else {
      childInputProps = {...childInputProps, disabled}
    }
  }

  const labelStyle = input && input.name === CustomSwitch.name ? {
    verticalAlign: "sub",
    marginRight: "10px"
  } : {}

  return (
    <Form.Group {...filteredProps} controlId={name} style={{...style, height: "100%"}}>
      {label && <Form.Label style={labelStyle}>{label}</Form.Label>}
      {input ? inputFromProps() : inputFromChildren()}
      {text && !disabled && <Form.Text className="text-muted">{text}</Form.Text>}
      {errors && errors.map( (error, index) => (
        <Form.Control.Feedback key={index} type="invalid">
          {error}
        </Form.Control.Feedback>))}
    </Form.Group>
  )
});

FormGroup.propTypes = {
  children: (props) => {
    if (!props.input && !props.children) {
      return new Error(`One among input or children prop must be provided`);
    }
  },
  input: (props) => {
    PropTypes.oneOf(['number', 'text', PropTypes.element]);
    if (!props.input && !props.children) {
      return new Error(`One among input or children prop must be provided`);
    }
  },
  as: PropTypes.elementType,
  label: PropTypes.node,
  placeholder: PropTypes.node,
  onChange: PropTypes.func.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.array, PropTypes.object]),
  disabled: PropTypes.bool,
  errors: PropTypes.arrayOf(PropTypes.string),
  isValid: PropTypes.bool,
  name: PropTypes.string.isRequired,
  text: PropTypes.node,
  xs: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  sm: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  md: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  lg: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
  xl: PropTypes.oneOfType([PropTypes.number, PropTypes.object])
}

FormGroup.defaultProps = {
  disabled: false
};

export default FormGroup;
